/**
 * Firestore persistence helpers for child progress and badges.
 * These write to subcollections under each child document:
 *   users/{uid}/children/{childId}/progress/{pathwayId}
 *   users/{uid}/children/{childId}/badges/{badgeId}
 *   users/{uid}/children/{childId}/lessonBadges/{lessonId}
 */
import { doc, setDoc, getDocs, collection, serverTimestamp } from 'firebase/firestore';
import { db, isFirebaseConfigured } from './config';
import { logError } from '../utils/errorLogger';
import type { Progress, EarnedBadge, EarnedLessonBadge } from '../types';

// ── Save progress ────────────────────────────────────────────────────────────

export async function saveProgress(
  userId: string,
  childId: string,
  pathwayId: string,
  progress: Progress,
) {
  if (!isFirebaseConfigured()) return;
  try {
    const ref = doc(db, 'users', userId, 'children', childId, 'progress', pathwayId);
    await setDoc(ref, {
      ...progress,
      lastActiveAt: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    logError('persistence', err, { action: 'saveProgress', userId, childId, pathwayId });
  }
}

// ── Save badge ───────────────────────────────────────────────────────────────

export async function saveBadge(
  userId: string,
  childId: string,
  badge: EarnedBadge,
) {
  if (!isFirebaseConfigured()) return;
  try {
    const ref = doc(db, 'users', userId, 'children', childId, 'badges', badge.id);
    await setDoc(ref, {
      id: badge.id,
      earnedAt: serverTimestamp(),
    });
  } catch (err) {
    logError('persistence', err, { action: 'saveBadge', userId, childId, badgeId: badge.id });
  }
}

export async function saveLessonBadge(
  userId: string,
  childId: string,
  badge: EarnedLessonBadge,
) {
  if (!isFirebaseConfigured()) return;
  try {
    const ref = doc(db, 'users', userId, 'children', childId, 'lessonBadges', badge.lessonId);
    await setDoc(ref, {
      lessonId: badge.lessonId,
      earnedAt: serverTimestamp(),
    });
  } catch (err) {
    logError('persistence', err, { action: 'saveLessonBadge', userId, childId, lessonId: badge.lessonId });
  }
}

// ── Load progress for a child ────────────────────────────────────────────────

export async function loadProgress(
  userId: string,
  childId: string,
): Promise<Record<string, Progress>> {
  if (!isFirebaseConfigured()) return {};
  try {
    const ref = collection(db, 'users', userId, 'children', childId, 'progress');
    const snap = await getDocs(ref);
    const result: Record<string, Progress> = {};
    snap.docs.forEach((d) => {
      const data = d.data();
      result[d.id] = {
        pathwayId:        d.id,
        lessonsCompleted: data.lessonsCompleted ?? [],
        currentLessonId:  data.currentLessonId ?? null,
        startedAt:        data.startedAt?.toDate?.() ?? new Date(),
        lastActiveAt:     data.lastActiveAt?.toDate?.() ?? new Date(),
        streakDays:       data.streakDays ?? 0,
        totalMinutes:     data.totalMinutes ?? 0,
        weeklyActivity:   data.weeklyActivity ?? {},
      };
    });
    return result;
  } catch (err) {
    logError('persistence', err, { action: 'loadProgress', userId, childId });
    return {};
  }
}

// ── Load badges for a child ──────────────────────────────────────────────────

export async function loadBadges(
  userId: string,
  childId: string,
): Promise<{ badges: EarnedBadge[]; lessonBadges: EarnedLessonBadge[] }> {
  if (!isFirebaseConfigured()) return { badges: [], lessonBadges: [] };
  try {
    const [badgeSnap, lessonBadgeSnap] = await Promise.all([
      getDocs(collection(db, 'users', userId, 'children', childId, 'badges')),
      getDocs(collection(db, 'users', userId, 'children', childId, 'lessonBadges')),
    ]);

    const badges: EarnedBadge[] = badgeSnap.docs.map((d) => ({
      id: d.id,
      earnedAt: d.data().earnedAt?.toDate?.() ?? new Date(),
    }));

    const lessonBadges: EarnedLessonBadge[] = lessonBadgeSnap.docs.map((d) => ({
      lessonId: d.id,
      earnedAt: d.data().earnedAt?.toDate?.() ?? new Date(),
    }));

    return { badges, lessonBadges };
  } catch (err) {
    logError('persistence', err, { action: 'loadBadges', userId, childId });
    return { badges: [], lessonBadges: [] };
  }
}
